import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Tooltip } from '@/components/ui/tooltip';
import { Lock, Coins, Trophy, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useAchievements } from '@/hooks/useAchievements';
import { validatePathPrerequisites, formatPrerequisiteMessage } from '@/utils/pathValidation';

interface PathMilestone {
  title: string;
  reward: number;
  completed?: boolean;
}

interface LearningPath {
  id: string;
  title: string;
  description: string;
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  progress: number;
  nextMilestone?: PathMilestone;
  requiredCoins?: number;
  isLocked?: boolean;
  prerequisites?: string[];
}

interface EnhancedLearningPathCardProps {
  path: LearningPath;
  onStart: () => void;
  profile: any; // Replace with your profile type
  isSelected?: boolean;
}

export function EnhancedLearningPathCard({ 
  path, 
  onStart, 
  profile,
  isSelected 
}: EnhancedLearningPathCardProps) {
  const [isUnlocking, setIsUnlocking] = useState(false);
  const userCoins = profile?.career_coins ?? 0;
  const { checkAchievements } = useAchievements();

  const handleUnlock = async () => {
    if (!path.requiredCoins) return;
    
    try {
      setIsUnlocking(true);
      
      // Check if user has enough coins
      if (userCoins < path.requiredCoins) {
        toast.error('Not enough coins to unlock this path!');
        return;
      }

      // Deduct coins and unlock path
      const { data, error } = await supabase
        .from('user_profiles')
        .update({ 
          career_coins: userCoins - path.requiredCoins,
          unlocked_paths: [...(profile.unlocked_paths || []), path.id]
        })
        .eq('id', profile.id)
        .single();

      if (error) throw error;

      // Record the transaction
      await supabase
        .from('coin_transactions')
        .insert({
          user_id: profile.id,
          amount: path.requiredCoins,
          type: 'spend',
          description: `Unlocked learning path: ${path.title}`
        });

      toast.success('Learning path unlocked successfully!');
      onStart();
    } catch (error) {
      console.error('Error unlocking path:', error);
      toast.error('Failed to unlock learning path');
    } finally {
      setIsUnlocking(false);
    }
  };

  const handleStart = async () => {
    try {
      // Validate prerequisites
      const validation = await validatePathPrerequisites(profile.id, path.id);
      
      if (!validation.isValid) {
        toast.error('Prerequisites not met', {
          description: formatPrerequisiteMessage(validation.missingPrerequisites)
        });
        return;
      }

      // Check for achievements
      await checkAchievements({
        type: 'learning',
        pathsStarted: profile.started_paths ? [...profile.started_paths, path.id] : [path.id]
      });

      onStart();
    } catch (error) {
      console.error('Error starting path:', error);
      toast.error('Failed to start learning path');
    }
  };

  return (
    <Card className={`relative overflow-hidden border-2 ${
      isSelected ? 'border-primary-500' : 'hover:border-primary-500'
    }`}>
      {/* Difficulty Badge */}
      <div className="absolute top-4 right-4 flex gap-2">
        <Badge variant={
          path.difficulty === 'beginner' ? 'success' :
          path.difficulty === 'intermediate' ? 'warning' : 'danger'
        }>
          {path.difficulty}
        </Badge>
        {path.isLocked && (
          <Badge variant="secondary">
            <Lock className="h-3 w-3 mr-1" />
            {path.requiredCoins} coins
          </Badge>
        )}
      </div>

      <CardHeader>
        <h3 className="text-xl font-bold">{path.title}</h3>
        <p className="text-gray-600">{path.description}</p>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress Section */}
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span>Progress</span>
            <span className="font-medium">{path.progress}%</span>
          </div>
          <div className="relative">
            <Progress value={path.progress} className="h-2" />
            {path.nextMilestone && (
              <Tooltip content={`Next milestone: ${path.nextMilestone.title}`}>
                <div 
                  className="absolute top-0 h-4 w-1 bg-primary-500"
                  style={{ left: `${path.nextMilestone.reward}%` }}
                />
              </Tooltip>
            )}
          </div>
        </div>

        {/* Prerequisites Warning */}
        {path.prerequisites && path.prerequisites.length > 0 && (
          <div className="text-sm text-amber-600">
            <p>Prerequisites: {path.prerequisites.join(', ')}</p>
          </div>
        )}

        {/* Action Button */}
        <motion.div
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Button
            className="w-full"
            variant={path.isLocked ? "secondary" : "default"}
            onClick={path.isLocked ? handleUnlock : handleStart}
            disabled={isUnlocking || (path.isLocked && userCoins < (path.requiredCoins || 0))}
          >
            <AnimatePresence mode="wait">
              {isUnlocking ? (
                <motion.div
                  key="unlocking"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center"
                >
                  <div className="animate-spin mr-2">⌛</div>
                  Unlocking...
                </motion.div>
              ) : path.isLocked ? (
                <motion.div
                  key="locked"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center"
                >
                  <Coins className="mr-2 h-4 w-4" />
                  Unlock Path
                </motion.div>
              ) : (
                <motion.div
                  key="start"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center"
                >
                  Continue
                  <ChevronRight className="ml-2 h-4 w-4" />
                </motion.div>
              )}
            </AnimatePresence>
          </Button>
        </motion.div>

        {/* Rewards Preview */}
        {path.nextMilestone && (
          <div className="flex items-center justify-between text-sm text-gray-600 mt-2">
            <span>Next Reward:</span>
            <div className="flex items-center">
              <Trophy className="h-4 w-4 text-yellow-500 mr-1" />
              <span>{path.nextMilestone.reward} coins</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
} 